import React, { useState, useEffect } from 'react';
import {
    Card, DatePicker, Select, Button, Row, Col, Statistic, Typography, Empty, message, Space
} from 'antd';
import {
    SearchOutlined, PieChartOutlined, ClockCircleOutlined, UserOutlined, CheckCircleOutlined, PauseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { reportService, userService } from '../../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const formatDuration = (s) => {
    if (s === null || s === undefined) return '0s';
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    parts.push(`${seconds}s`);
    return parts.join(' ');
};

const SimplePieChart = ({ data }) => {
    const total = data.reduce((acc, cur) => acc + cur.value, 0);
    if (total === 0) return <Empty description="Sin datos" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

    let currentAngle = 0;
    const segments = data.map((item) => {
        const angle = (item.value / total) * 360;
        const start = currentAngle;
        currentAngle += angle;
        return `${item.color} ${start}deg ${currentAngle}deg`;
    });

    const background = `conic-gradient(${segments.join(', ')})`;

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <div style={{
                width: 250, height: 250, borderRadius: '50%',
                background: background,
                position: 'relative',
                border: '1px solid #f0f0f0'
            }} />
            <div style={{ marginTop: 20, display: 'flex', gap: 15, flexWrap: 'wrap', justifyContent: 'center' }}>
                {data.map((d, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ width: 12, height: 12, backgroundColor: d.color, marginRight: 8, borderRadius: 2 }} />
                        <Text>{d.name}: {formatDuration(d.value)} ({((d.value/total)*100).toFixed(1)}%)</Text>
                    </div>
                ))}
            </div>
        </div>
    );
};

const ServiceTimeReport = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [agents, setAgents] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);

    // Filters state
    const [filters, setFilters] = useState({
        date_range: [dayjs().startOf('month'), dayjs().endOf('month')],
        user_id: undefined,
    });

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                // If not, we might need to use a general user list filtered by role.
                // Checking api.js or creating a specific endpoint is better.
                // For now, let's try userService.getAgents() if it exists or list users.
                // Based on previous files, we can use pbxReportCatalogService.getAgents if available 
                // or just standard user list. Let's assume standard users for now or create a helper.
                // Let's use a safe fallback or assume getAgents exists or I should check api.js more closely.
                // Checking api.js... userService has "list" method.
                const res = await userService.list({ role: 'agent' }); 
                if (res.status === 'success') {
                     setAgents(res.data.users || res.data); // Adjust based on actual response structure
                }
            } catch (error) {
                console.error("Error fetching agents", error);
            }
        };
        fetchAgents();
    }, []);

    const fetchData = async () => {
        if (!filters.user_id) {
            message.warning("Debe seleccionar un agente");
            return;
        }

        setLoading(true);
        setHasSearched(true);
        try {
            const params = {
                user_id: filters.user_id,
                date_range: filters.date_range ? filters.date_range.map(d => d.format('YYYY-MM-DD')).join(',') : null
            };
            const response = await reportService.getServiceTimeReport(params);
            if (response.status === 'success') {
                setData(response.data);
            } else {
                setData(null);
            }
        } catch (error) {
            console.error(error);
            message.error("Error al cargar reporte de tiempo de servicio");
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const statusColors = {
        available: '#52c41a', // Green
        incall: '#1890ff',    // Blue
        break: '#faad14',     // Orange/Yellow
        wrapup: '#722ed1',    // Purple
        ringing: '#13c2c2',   // Cyan
        connected: '#000000'
    };

    const chartData = data ? [
        { name: 'Disponible', value: data.available, color: statusColors.available },
        { name: 'En Llamada', value: data.incall, color: statusColors.incall },
        { name: 'Break', value: data.break, color: statusColors.break },
        { name: 'Wrapup', value: data.wrapup, color: statusColors.wrapup },
        { name: 'Timbrando', value: data.ringing, color: statusColors.ringing },
    ].filter(d => d.value > 0) : [];

    return (
        <div className="fade-in">
            <Card variant="borderless" style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Space wrap>
                    <Select
                        placeholder="Seleccione Agente"
                        value={filters.user_id}
                        onChange={(val) => setFilters({ ...filters, user_id: val })}
                        style={{ width: 250 }}
                        optionFilterProp="children"
                        showSearch
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {agents.map(u => <Option key={u.id} value={u.id}>{u.name} ({u.username})</Option>)}
                    </Select>
                    <RangePicker
                        value={filters.date_range}
                        onChange={(val) => setFilters({ ...filters, date_range: val })}
                        placeholder={['Inicio', 'Fin']}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={fetchData} loading={loading}>
                        Mostrar datos
                    </Button>
                </Space>
            </Card>

            {!hasSearched || !data ? (
                <Empty description={hasSearched ? "No se encontraron datos" : "Seleccione un agente para ver el reporte"} />
            ) : (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {/* Cards Row */}
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={8} md={4}>
                            <Card>
                                <Statistic 
                                    title="Total Conectado" 
                                    value={formatDuration(data.connected)} 
                                    prefix={<ClockCircleOutlined />} 
                                    valueStyle={{ color: '#000000', fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8} md={4}>
                            <Card>
                                <Statistic 
                                    title="Disponible" 
                                    value={formatDuration(data.available)} 
                                    prefix={<CheckCircleOutlined />} 
                                    valueStyle={{ color: statusColors.available, fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8} md={4}>
                            <Card>
                                <Statistic 
                                    title="En Llamada" 
                                    value={formatDuration(data.incall)} 
                                    prefix={<UserOutlined />} 
                                    valueStyle={{ color: statusColors.incall, fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8} md={4}>
                            <Card>
                                <Statistic 
                                    title="Break" 
                                    value={formatDuration(data.break)} 
                                    prefix={<PauseCircleOutlined />} 
                                    valueStyle={{ color: statusColors.break, fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8} md={4}>
                            <Card>
                                <Statistic 
                                    title="Wrapup" 
                                    value={formatDuration(data.wrapup)} 
                                    prefix={<ClockCircleOutlined />} 
                                    valueStyle={{ color: statusColors.wrapup, fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={8} md={4}>
                            <Card>
                                <Statistic 
                                    title="Timbrando" 
                                    value={formatDuration(data.ringing)} 
                                    prefix={<ClockCircleOutlined />} 
                                    valueStyle={{ color: statusColors.ringing, fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Chart Row */}
                    <Row gutter={[24, 24]} justify="center">
                        <Col xs={24} lg={12}>
                            <Card title={<Space><PieChartOutlined /> Distribución de Tiempos</Space>} style={{ height: '100%' }}>
                                <SimplePieChart data={chartData} />
                            </Card>
                        </Col>
                    </Row>
                </Space>
            )}
        </div>
    );
};

export default ServiceTimeReport;
